import React, { useState } from 'react';
import { X, Wrench, Calendar, DollarSign, FileText, User, Clock } from 'lucide-react';
import { getTodayAEST } from '../../utils/dateUtils';

interface AssetWithServices {
  id: string;
  name: string;
  type: string;
  asset_type: 'vehicle' | 'plant';
  status: string;
  current_hours?: number;
  current_km?: number;
  service_interval_hours?: number;
  service_interval_km?: number;
  service_interval_months?: number;
}

interface ServiceRecord {
  id: string;
  asset_id: string;
  asset_type: 'vehicle' | 'plant';
  service_date: string;
  service_type: string;
  hours_at_service?: number;
  km_at_service?: number;
  next_due_date?: string;
  next_due_hours?: number;
  next_due_km?: number;
  service_provider?: string;
  cost?: number;
  description: string;
  notes?: string;
}

interface ServiceRecordFormProps {
  asset: AssetWithServices;
  service?: ServiceRecord | null;
  onSubmit: (serviceData: Partial<ServiceRecord>) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
}

export const ServiceRecordForm: React.FC<ServiceRecordFormProps> = ({ 
  asset,
  service, 
  onSubmit, 
  onClose, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    asset_id: asset.id,
    asset_type: asset.asset_type,
    service_date: service?.service_date || getTodayAEST(),
    service_type: service?.service_type || 'routine',
    hours_at_service: service?.hours_at_service?.toString() || asset.current_hours?.toString() || '',
    km_at_service: service?.km_at_service?.toString() || asset.current_km?.toString() || '',
    service_provider: service?.service_provider || '',
    cost: service?.cost?.toString() || '',
    description: service?.description || '',
    notes: service?.notes || ''
  });

  const [calculatedNextDue, setCalculatedNextDue] = useState({
    next_due_date: '',
    next_due_hours: '',
    next_due_km: ''
  });

  // Auto-calculate next due dates when service date, hours, or km change
  React.useEffect(() => {
    calculateNextDueDates();
  }, [formData.service_date, formData.hours_at_service, formData.km_at_service, formData.service_type]);

  const isScheduledService = (type: string) => type === 'routine' || type === 'major';

  const calculateNextDueDates = () => {
    let nextDueDate = '';
    let nextDueHours = '';
    let nextDueKm = '';

    if (isScheduledService(formData.service_type)) {
      // Calculate next due date based on service interval
      if (formData.service_date && asset.service_interval_months) {
        const serviceDate = new Date(formData.service_date);
        const nextDate = new Date(serviceDate);
        nextDate.setMonth(serviceDate.getMonth() + asset.service_interval_months);
        nextDueDate = nextDate.toISOString().split('T')[0];
      }

      // Calculate next due hours based on service interval
      if (formData.hours_at_service && asset.service_interval_hours) {
        const hoursAtService = Number(formData.hours_at_service);
        nextDueHours = (hoursAtService + asset.service_interval_hours).toString();
      }

      // Calculate next due kilometers based on service interval
      if (formData.km_at_service && asset.service_interval_km) {
        const kmAtService = Number(formData.km_at_service);
        nextDueKm = (kmAtService + asset.service_interval_km).toString();
      }
    }

    setCalculatedNextDue({ next_due_date: nextDueDate, next_due_hours: nextDueHours, next_due_km: nextDueKm });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const serviceData = {
      asset_id: asset.id,
      asset_type: asset.asset_type,
      service_date: formData.service_date,
      service_type: formData.service_type,
      hours_at_service: formData.hours_at_service ? Number(formData.hours_at_service) : null,
      km_at_service: formData.km_at_service ? Number(formData.km_at_service) : null,
      next_due_date: calculatedNextDue.next_due_date || null,
      next_due_hours: calculatedNextDue.next_due_hours ? Number(calculatedNextDue.next_due_hours) : null,
      next_due_km: calculatedNextDue.next_due_km ? Number(calculatedNextDue.next_due_km) : null,
      service_provider: formData.service_provider || null,
      cost: formData.cost ? Number(formData.cost) : null,
      description: formData.description,
      notes: formData.notes || null
    };

    await onSubmit(serviceData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Wrench className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {service ? 'Edit Service Record' : 'Add Service Record'}
              </h3>
              <p className="text-sm text-gray-500">{asset.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Asset Information Display */}
          <div className={`rounded-lg p-4 ${
            asset.asset_type === 'vehicle' ? 'bg-blue-50 border border-blue-200' : 'bg-purple-50 border border-purple-200'
          }`}>
            <h4 className={`font-medium mb-2 ${
              asset.asset_type === 'vehicle' ? 'text-blue-900' : 'text-purple-900'
            }`}>
              {asset.asset_type === 'vehicle' ? 'Vehicle' : 'Plant/Machinery'} Information
            </h4>
            <div className={`text-sm space-y-1 ${
              asset.asset_type === 'vehicle' ? 'text-blue-800' : 'text-purple-800'
            }`}>
              <p><strong>Name:</strong> {asset.name}</p>
              <p><strong>Type:</strong> {asset.type.charAt(0).toUpperCase() + asset.type.slice(1)}</p>
              <p><strong>Status:</strong> {asset.status.charAt(0).toUpperCase() + asset.status.slice(1)}</p>
              {asset.current_hours != null && (
                <p><strong>Current Hours:</strong> {asset.current_hours.toFixed(1)} hours</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />
                Service Date *
              </label>
              <input
                type="date"
                name="service_date"
                required
                value={formData.service_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Type *
              </label>
              <select
                name="service_type"
                required
                value={formData.service_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="routine">Routine Service</option>
                <option value="major">Major Service</option>
                <option value="repair">Repair</option>
                <option value="inspection">Inspection</option>
                <option value="breakdown">Breakdown</option>
                <option value="warranty">Warranty Work</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="inline h-4 w-4 mr-1" />
                Service Provider
              </label>
              <input
                type="text"
                name="service_provider"
                value={formData.service_provider}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="ABC Mechanical Services"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <DollarSign className="inline h-4 w-4 mr-1" />
                Service Cost
              </label>
              <input
                type="number"
                name="cost"
                min="0"
                step="0.01"
                value={formData.cost}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="0.00"
              />
            </div>
          </div>

          {asset.asset_type === 'plant' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="inline h-4 w-4 mr-1" />
                Hours at Service
              </label>
              <input
                type="number"
                name="hours_at_service"
                min="0"
                step="0.1"
                value={formData.hours_at_service}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Current operating hours"
              />
              <p className="text-xs text-gray-500 mt-1">
                Operating hours at the time of service (used to calculate next service due)
              </p>
            </div>
          )}

          {asset.asset_type === 'vehicle' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="inline h-4 w-4 mr-1" />
                  Kilometers at Service
                </label>
                <input
                  type="number"
                  name="km_at_service"
                  min="0"
                  step="0.01"
                  value={formData.km_at_service}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Current odometer reading"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Odometer reading at time of service
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="inline h-4 w-4 mr-1" />
                  Hours at Service
                </label>
                <input
                  type="number"
                  name="hours_at_service"
                  min="0"
                  step="0.1"
                  value={formData.hours_at_service}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Current engine hours"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Engine hours at time of service
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Description *
            </label>
            <textarea
              name="description"
              required
              rows={3}
              value={formData.description}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Describe the service work performed..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes
            </label>
            <textarea
              name="notes"
              rows={2}
              value={formData.notes}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Any additional notes or observations..."
            />
          </div>

          {/* Next Service Due Preview */}
          {(calculatedNextDue.next_due_date || calculatedNextDue.next_due_hours || calculatedNextDue.next_due_km) && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-900 mb-2">Next Service Due</h4>
              <div className="text-sm text-green-800 space-y-1">
                {calculatedNextDue.next_due_date && (
                  <p><strong>Date:</strong> {new Date(calculatedNextDue.next_due_date).toLocaleDateString()}</p>
                )}
                {calculatedNextDue.next_due_hours && (
                  <p><strong>Hours:</strong> {calculatedNextDue.next_due_hours} hours</p>
                )}
                {calculatedNextDue.next_due_km && (
                  <p><strong>Kilometers:</strong> {Number(calculatedNextDue.next_due_km).toLocaleString()} km</p>
                )}
              </div>
            </div>
          )}

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Saving...' : (service ? 'Update Service' : 'Add Service Record')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};